import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import PageCTA from '../../components/feature/PageCTA';

export default function Tier5FullSuccessIntelligencePage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-rose-600 via-pink-600 to-purple-700 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex items-center justify-center mb-6">
            <span className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full text-sm font-semibold">
              <i className="ri-vip-crown-fill text-2xl"></i>
              PREMIUM TIER
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-center mb-6">
            Tier 5 — Full Success Intelligence™
          </h1>
          <p className="text-xl md:text-2xl text-center text-white/90 max-w-4xl mx-auto mb-12">
            The most advanced, complete app evaluation available. All seven intelligence systems, global scoring, competitive analysis, and investor-grade insights.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <span className="text-4xl font-bold">$2,499</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link
              to="/submit-app"
              className="inline-block bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-4 rounded-xl font-semibold text-base hover:from-emerald-700 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get Full Success Intelligence
            </Link>
            <Link
              to="/sample-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-lg font-semibold hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              Download Sample Report
            </Link>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-900">
            Who This Is For
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Founders ready for big traction',
              'Apps with real users or strong potential',
              'Founders planning to scale',
              'Investor-ready founders',
              'High-level builders needing full clarity',
              'Serious competitors aiming for dominance'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-6 bg-white rounded-xl shadow-sm">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-2xl text-rose-600"></i>
                </div>
                <p className="text-lg text-gray-700 flex-1">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">
            The Full Intelligence Suite — Nothing Held Back
          </h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto">
            Every system, every insight, every advantage — all in one comprehensive evaluation.
          </p>

          <div className="space-y-8">
            {/* All 7 Intelligence Systems */}
            <div className="bg-gradient-to-br from-rose-50 to-purple-50 rounded-2xl p-8 border-2 border-rose-200">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 flex items-center justify-center bg-rose-600 rounded-xl flex-shrink-0">
                  <i className="ri-brain-fill text-3xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    All 7 Intelligence Systems Combined
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      'Expectation Modeling',
                      'Cognitive Load Simulation',
                      'Friction Point Indicators',
                      'Emotional Flow Resonance',
                      'Desire Trigger Scoring',
                      'Category Standards Benchmarking',
                      'Narrative & Value Alignment'
                    ].map((system, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <i className="ri-arrow-right-circle-fill text-rose-600"></i>
                        <span className="text-gray-700 font-medium">{system}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Other Features */}
            {[
              {
                icon: 'ri-line-chart-fill',
                title: 'Full Competitive Intelligence Profile',
                description: 'Your true position among competitors, mapped with benchmark accuracy.'
              },
              {
                icon: 'ri-global-fill',
                title: 'Global Growth Indicators & Market Fit Map',
                description: 'Predicts traction across countries and expansion potential.'
              },
              {
                icon: 'ri-shield-check-fill',
                title: 'Opportunity + Threat Matrix',
                description: 'Reveals pathways for growth and areas where competitors can overtake you.'
              },
              {
                icon: 'ri-medal-fill',
                title: 'Full App Credit Score™',
                description: 'Your complete performance and traction score.'
              },
              {
                icon: 'ri-verified-badge-fill',
                title: 'Full Validation Passport™',
                description: 'Your verified market readiness certification.'
              },
              {
                icon: 'ri-funds-fill',
                title: 'Investor Readiness Indicator',
                description: 'Shows how prepared your app is for investor conversations.'
              },
              {
                icon: 'ri-lightbulb-flash-fill',
                title: '3 High-Impact Strategic Recommendations',
                description: 'Direct, critical moves that reshape your progress immediately.'
              },
              {
                icon: 'ri-user-voice-fill',
                title: 'Optional 1:1 Review (Top Tier Only)',
                description: 'If selected, you receive a founder-to-expert clarity session.'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-rose-300 transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-rose-100 to-purple-100 rounded-xl flex-shrink-0">
                    <i className={`${feature.icon} text-3xl text-rose-600`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-lg text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-8">
            Why It Matters
          </h2>
          <div className="space-y-6 text-xl text-gray-300">
            <p className="text-2xl font-semibold text-white">
              Tier 5 gives you everything.
            </p>
            <p>No guessing.</p>
            <p>No missing pieces.</p>
            <p>No blind spots.</p>
            <p className="text-2xl font-semibold text-white pt-6">
              Just full clarity — growth, competition, user psychology, global potential, and investor-grade insights.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <PageCTA 
        title="Get the Full Success Intelligence Report"
        description="The most comprehensive app evaluation available — nothing held back."
        primaryText="Get Full Success Intelligence"
        primaryLink="/submit-app"
        bgGradient="from-rose-600 via-pink-600 to-purple-700"
      />

      <Footer />
    </div>
  );
}
